import { Divider, Tag } from "antd";
import {
  BarChart3,
  Building2,
  Calendar,
  CreditCard,
  FileText,
  Handshake,
  Hash,
  Landmark,
  Layers,
  MapPin,
  Network,
  Paperclip,
  Phone,
  Shield,
  User,
  Users,
} from "lucide-react";

import { type AgentDetailVO } from "@/api/agents/type";
import { DetailRow } from "@/components/DetailBlocks";
import { useDict } from "@/hooks/useDict";

import { AGENT_LEVEL_THEME, AGENT_STATUS_THEME } from "../../constants";

/** 代理详情头部名片 */
export const AgentHeaderCard = ({ agent }: { agent: AgentDetailVO }) => {
  const { getDictLabel } = useDict();
  // 等级配色
  const levelTheme = AGENT_LEVEL_THEME[agent.agentLevel];
  // 合作状态配色
  const statusTheme = agent.cooperationStatus
    ? AGENT_STATUS_THEME[agent.cooperationStatus]
    : undefined;

  return (
    <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl border">
      <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-sm shrink-0 bg-linear-to-br from-slate-500 to-slate-700">
        <Network className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className="font-bold text-lg truncate m-0">{agent.agentName || "-"}</h3>
          <Tag bordered color={levelTheme?.color} className="m-0 text-[10px]">
            {getDictLabel("agentLevel", agent.agentLevel)}
          </Tag>
          {agent.cooperationStatus && (
            <Tag bordered color={statusTheme?.color} className="m-0 text-[10px]">
              {getDictLabel("agentCoopStatus", agent.cooperationStatus)}
            </Tag>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
          <span className="font-mono">{agent.agentCode}</span>
          <span className="truncate">{agent.agentShortName}</span>
        </div>
      </div>
    </div>
  );
};

/** 基本信息 Tab */
export const AgentBasicTab = ({
  agent,
  isEditMode,
  onOpenAccount,
}: {
  agent: AgentDetailVO;
  isEditMode: boolean;
  /** 打开关联账号详情 */
  onOpenAccount?: (accountId: string) => void;
}) => {
  return (
    <div className="space-y-5">
      <div>
        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 m-0">
          <Network className="w-4 h-4 text-slate-400" />
          基本信息
        </h4>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
          <DetailRow icon={Network} label="代理名称" value={agent.agentName} isEdit={isEditMode} />
          <DetailRow
            icon={Network}
            label="代理简称"
            value={agent.agentShortName}
            isEdit={isEditMode}
          />
          <DetailRow icon={FileText} label="代理编号" value={agent.agentCode} isEdit={isEditMode} />
          <DetailRow
            icon={Layers}
            label="代理等级"
            type="dict"
            dictCode="agentLevel"
            value={agent.agentLevel}
            isEdit={isEditMode}
          />
          <DetailRow
            icon={User}
            label="管理员"
            value={agent.managerName || "—"}
            isEdit={isEditMode}
            onClick={
              agent.bindInfo?.userId && onOpenAccount && agent.managerName
                ? () => onOpenAccount(agent.bindInfo!.userId!)
                : undefined
            }
          />
          <DetailRow
            icon={Phone}
            label="负责人手机"
            value={agent.managerPhone || "—"}
            isEdit={isEditMode}
          />
          <DetailRow
            icon={Shield}
            label="合作状态"
            type="dict"
            dictCode="agentCoopStatus"
            value={agent.cooperationStatus}
            isEdit={isEditMode}
          />
        </div>
      </div>
    </div>
  );
};

/** 经营信息 Tab */
export const AgentBusinessTab = ({
  agent,
  isEditMode,
  onOpenStoreList,
}: {
  agent: AgentDetailVO;
  isEditMode: boolean;
  onOpenStoreList: (id: string) => void;
}) => {
  return (
    <div className="space-y-5">
      <div>
        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 m-0">
          <MapPin className="w-4 h-4 text-slate-400" />
          代理范围
        </h4>
        <DetailRow
          icon={MapPin}
          label="所在地区"
          value={agent.regionCodeNames?.join("、") || "—"}
          isEdit={isEditMode}
        />
        <DetailRow
          icon={MapPin}
          label="详细地址"
          value={agent.specificLocation}
          isEdit={isEditMode}
        />
      </div>
      <Divider className="my-0" />
      <div>
        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 m-0">
          <BarChart3 className="w-4 h-4 text-slate-400" />
          经营数据
        </h4>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
          <DetailRow
            icon={Building2}
            label="下辖门店"
            value={`${agent.shopCount ?? 0} 家`}
            onClick={
              agent?.shopCount && agent.shopCount > 0 ? () => onOpenStoreList(agent.id) : undefined
            }
          />
          <DetailRow icon={Users} label="下辖员工" value={`${agent.employeeCount ?? 0} 人`} />
        </div>
      </div>
    </div>
  );
};

/** 关联信息 Tab */
export const AgentInfoTab = ({
  agent,
  onOpenAccount,
  onOpenEntity,
}: {
  agent: AgentDetailVO;
  isEditMode: boolean;
  onOpenAccount?: (accountId: string) => void;
  onOpenEntity?: (entityId: string) => void;
}) => {
  return (
    <div>
      <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 m-0">
        <Building2 className="w-4 h-4 text-slate-400" />
        关联工商实体
      </h4>

      {agent.bindInfo?.businessEntityName ? (
        <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
          <div className="w-10 h-10 rounded-md bg-orange-500/10 flex items-center justify-center">
            <Landmark className="w-5 h-5 text-orange-500" />
          </div>
          <div className="flex-1 min-w-0">
            <button
              type="button"
              className="text-sm font-medium hover:underline text-orange-500 cursor-pointer truncate"
              onClick={
                agent.bindInfo?.businessEntityId && onOpenEntity
                  ? () => onOpenEntity(agent.bindInfo!.businessEntityId!)
                  : undefined
              }
            >
              {agent.bindInfo?.businessEntityName}
            </button>
            <p className="text-xs text-muted-foreground mt-0.5">工商实体名称</p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">未关联工商实体</p>
      )}
      <Divider className="my-0" />
      <div>
        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 m-0">
          <User className="w-4 h-4 text-slate-400" />
          关联账号
        </h4>
        {agent.bindInfo?.accountDisplayName ? (
          <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
            <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center">
              <User className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <button
                type="button"
                className="text-sm font-medium hover:underline text-orange-500 cursor-pointer"
                onClick={
                  agent.bindInfo?.userId && onOpenAccount
                    ? () => onOpenAccount(agent.bindInfo!.userId!)
                    : undefined
                }
              >
                {agent.bindInfo?.accountDisplayName}
              </button>
              <p className="text-xs text-muted-foreground mt-0.5">平台归属自然人</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">未关联账号</p>
        )}
      </div>
    </div>
  );
};

/** 银行与开票 Tab */
export const AgentBankTab = ({ agent }: { agent: AgentDetailVO }) => (
  <div>
    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 m-0">
      <CreditCard className="w-4 h-4 text-slate-400" />
      银行开户信息
    </h4>
    <div className="grid grid-cols-2 gap-x-6 gap-y-1">
      <DetailRow icon={User} label="收款名称" value={agent.accountName} />
      <DetailRow icon={CreditCard} label="收款卡号" value={agent.accountNo} />
      <DetailRow icon={Building2} label="银行名称" value={agent.bankCode} />
    </div>
    <Divider className="my-0" />
    <div>
      <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 m-0">
        <CreditCard className="w-4 h-4 text-slate-400" />
        开票信息
      </h4>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
        <DetailRow icon={FileText} label="发票抬头" value={agent.invoiceTitle} />
        <DetailRow icon={Hash} label="纳税人识别号" value={agent.taxId} />
      </div>
    </div>
  </div>
);

/**合作与其他 */
export const AgentCooperationTab = ({ agent }: { agent: AgentDetailVO }) => (
  <div>
    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 m-0">
      <Handshake className="w-4 h-4 text-slate-400" />
      合作信息
    </h4>
    <div className="grid grid-cols-2 gap-x-6 gap-y-1">
      <DetailRow icon={Calendar} label="签约日期" value={agent.contractSignedAt} />
      <DetailRow icon={Calendar} label="合同到期日" value={agent.contractExpireAt} />
      <DetailRow
        icon={CreditCard}
        label="代理模式"
        type="dict"
        dictCode="agentMode"
        value={agent.agentMode}
      />
    </div>
    <Divider className="my-0" />
    <div>
      <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 m-0">
        <FileText className="w-4 h-4 text-slate-400" />
        合同信息
      </h4>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
        <DetailRow icon={FileText} label="合同编号" value={agent.contractSignedAt} />
        <DetailRow icon={Paperclip} label="合同附件" value={agent.contractFiles} />
      </div>
    </div>
  </div>
);
